import uniqBy from "lodash/uniqBy";

import {getCommonVisualizerType} from "../../config";
import {getLookList} from "../look/lookNavigation/lookFunctions";
import {getCurrentLayout} from "../appLayout";
import {loadThumbnail} from "./aiThumbnailsLoad";
import {FEATURE_AREAS} from "./aiFeatureAreasConstants";
import {COLOR_ONLY_PROMPT_GROUPS, COLOR_ONLY_PROMPT_PART_SUFFIX, COLOR_2_PROMPT_PART_SUFFIX} from "./aiColorOnlyGroupsConstants";
import snakeCase from "lodash/snakeCase";
import {getDocumentFeatures} from "../../misc/quoteDocumentFeatures";

export const saveButtonIsEnabled = (popupContext) => {
  return !!popupContext.userFile && popupContext.promptsList && popupContext.promptsList.some(pr => pr.isActive);
}

export const imageIsRenderedAndUserIsNotLoggedIn = (showLeadOverlay, aiResults) => {
  return showLeadOverlay && aiResults.some(res => res.resultImageUrl);
}

export const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    const dataUrl = reader.result;
    const base64Data = dataUrl.split(',')[1]; // ← This extracts just the image part
    resolve(base64Data);
  };
  reader.onerror = reject;
});

export const getExportAIData = (aiState, groupObjects, aiResults) => {
  const visualizerType = getCommonVisualizerType();
  let exportData = {
    ...aiState.aiExportData,
    aiResults: (aiResults || aiState.aiResults).map(result => {
      // Everything else the backend returned is exported as is (isMultiImage, prompts, ...),
      // so a restored job gets the very same result objects back - they are applied to the
      // state by setLookAiImportData. Whitelisting fields here silently drops any new flag.
      const exportedResult = {...result};
      // a blob:/base64 "before" image only lives as long as the current page - a restored job
      // can not load it, so it is dropped from the export. It is blanked on the COPY on
      // purpose: this runs from the SET_AI_RESULTS reducer, so mutating `result` would wipe
      // sourceImageUrl on the live result too and the before/after slider would lose its
      // "before" image right after a generation (see LookAIImageOverlayImageContent).
      if (exportedResult.sourceImageUrl?.includes("base64")
        || exportedResult.sourceImageUrl?.includes("blob:http")) {
        exportedResult.sourceImageUrl = "";
      }
      // the only exception: "files" carries the base64 of every image sent to OpenAI (used by
      // the dev popup only, see UiPopupAIContentResult) and would bloat the saved job
      delete exportedResult.files;
      return exportedResult;
    })
  };
  if (Array.isArray(groupObjects) && groupObjects.length > 0) {
    exportData.aiGroups = {
      ...(exportData.aiGroups || {}),
      [visualizerType]: (groupObjects || []).map(groupObject => {
        return {
          partId: groupObject.partId,
          enabled: groupObject.enabled,
          groupName: groupObject.groupName || groupObject.featureGroup?.dbName || groupObject.featureGroup?.partName,
        }
      })
    }
  }
  // console.log("exportData", exportData);
  return exportData
}

/**
 * returns the features selected in the AI panel ("View it in your ...!"), to be stored with the
 * generated image - the active feature of every group object switched on there
 * @param groupObjects {array} the enabled group objects the image is generated from
 * @returns {[{groupName: string, displayName: string}]}
 */
export const getAiGroupsDocumentFeatures = (groupObjects) => {
  if (!Array.isArray(groupObjects)) {
    return [];
  }
  return getDocumentFeatures(groupObjects.map(groupObject => {
    return groupObject.featureGroup && groupObject.featureGroup.activeFeature
      ? groupObject.featureGroup.activeFeature.dbFeature
      : null
  }));
}

export const hideGroupObject = (groupObject, showAllGroups = false) => {
  if (showAllGroups) {
    return !groupObject
  }
  return !groupObject
    || groupObject.hideIfShowAllIsNotEnabled
    || !groupObject.featureGroup
    || (groupObject.featureGroup.hideLayerCondition && groupObject.featureGroup.hideLayerCondition())
    || (groupObject.featureGroup.activeFeature && !groupObject.featureGroup.activeFeature.layer.src)
}

/**
 * Given the partIds that make up an AI feature area (e.g. "Wet Area"), returns only the
 * ones that are currently active in the layout - i.e. rendered as a layer. Shared between
 * the manual toggle in UiPopupAIContentFeaturesAreas and setDefaultAiFeatureAreasGroups so
 * both compute "which groups are active right now" the same way.
 */
export const getActivePartIdsForFeatureArea = (availablePartIds, layout) => {
  const layers = layout.getLayers();
  return availablePartIds.filter(partId => layers.some(l => l.partId === partId));
}

/**
 * Synthetic "<Group Name>_ColorOnly" and "<Group Name>_Color 2" group objects for the AI
 * Management dev popup, for the given dev group object when its feature group is one of
 * COLOR_ONLY_PROMPT_GROUPS and the fabricator has aiSettings.colorOnly on. Each one's
 * `featureGroup` is a stand-in that only carries the prompt part name, so `baseFeatureGroup`
 * keeps a reference to the real cabinet group and hideDevGroupObject resolves visibility
 * against it - the rows belong to that group and must not be listed on their own when the
 * group isn't. They carry no enabled / sendThumbnail controls, so `enabled` stays true and
 * `sendThumbnail` stays false.
 */
const getColorOnlyGroupObjects = (devGroupObject, aiSettings) => {
  if (!aiSettings || !aiSettings.colorOnly) {
    return [];
  }
  const {featureGroup} = devGroupObject;
  const colorOnlyGroup = COLOR_ONLY_PROMPT_GROUPS.find(group => group.partId === featureGroup.partId);
  if (!colorOnlyGroup) {
    return [];
  }
  const colorOnlySuffixes = [
    {promptPartSuffix: COLOR_ONLY_PROMPT_PART_SUFFIX, partIdSuffix: "_color_1"},
    {promptPartSuffix: COLOR_2_PROMPT_PART_SUFFIX, partIdSuffix: "_color_2"},
  ];
  return colorOnlySuffixes.map(({promptPartSuffix, partIdSuffix}) => {
    const promptPartName = colorOnlyGroup.groupName + promptPartSuffix;
    const partId = featureGroup.partId + partIdSuffix;
    return {
      featureGroup: {
        partId,
        partName: promptPartName,
        dbName: promptPartName,
      },
      baseFeatureGroup: featureGroup,
      hideIfShowAllIsNotEnabled: devGroupObject.hideIfShowAllIsNotEnabled,
      partId,
      enabled: true,
      sendThumbnail: false,
      isColorOnlyPromptPart: true,
    };
  });
};

/**
 * hideGroupObject for the AI Management dev popup. Rows carrying a `baseFeatureGroup` (the
 * "_ColorOnly" ones) have a stand-in featureGroup with no layer of its own, so they are shown
 * or hidden exactly when the group they belong to is.
 */
export const hideDevGroupObject = (groupObject, showAllGroups = false) => {
  if (groupObject && groupObject.baseFeatureGroup) {
    return hideGroupObject({
      ...groupObject,
      featureGroup: groupObject.baseFeatureGroup,
    }, showAllGroups);
  }
  return hideGroupObject(groupObject, showAllGroups);
};

export const getAIInitialGroups = (aiImportData, aiSettings, forDevSettings = false) => {
  const lookList = getLookList();
  const layout = getCurrentLayout();
  const visualizerType = getCommonVisualizerType();

  const importAiGroups = aiImportData && aiImportData.aiGroups && aiImportData.aiGroups[visualizerType];
  const hasImportAiGroups = Array.isArray(importAiGroups) && importAiGroups.length > 0;

  const baseAiGroups = Array.isArray(aiSettings.aiGroups)
    ? aiSettings.aiGroups
    : typeof aiSettings.aiGroups === "object" && aiSettings.aiGroups ? aiSettings.aiGroups[visualizerType] || [] : [];

  // aiSettings.aiGroups (the fabricator-configured group list) decides *which* groups
  // exist, and takes priority over aiImportData.aiGroups[visualizerType] (a previously
  // saved job's group list) - except any import group missing from aiSettings.aiGroups
  // is still appended below, so it isn't lost. aiSettings.aiGroups may just be an array
  // of groupName strings, e.g. ['Countertop', 'Faucet'], so matching here is by groupName.
  const baseGroupNames = new Set(
    baseAiGroups.map(group => (group && typeof group === "object" ? group.groupName : group))
  );
  const missingImportGroups = hasImportAiGroups
    ? importAiGroups.filter(group => group && typeof group === "object" && !baseGroupNames.has(group.groupName))
    : [];

  const aiGroups = [...baseAiGroups, ...missingImportGroups];
  const groupsList = aiGroups.length ? aiGroups.map(group => {
    if (typeof group === "object") {
      const featureGroup = layout.getFeatureGroupByGroupName(group.groupName);
      if (featureGroup) {
        return {
          ...group,
          featureGroup,
        }
      }
    }
    const featureGroup = layout.getFeatureGroupByGroupName(group);
    if (featureGroup) {
      return {
        featureGroup,
        partId: featureGroup.partId,
      }
    }
  }).filter(groupName => groupName) : lookList;

  if (forDevSettings) {
    const allFetureGroups = layout.getAllFeatureGroups();
    // each cabinet group with a "_ColorOnly" prompt part gets that extra row right below it
    const devGroups = allFetureGroups.flatMap(featureGroup => {
      const devGroup = {
        featureGroup,
        partId: featureGroup.partId,
        enabled: true,
        hideIfShowAllIsNotEnabled: !groupsList.some(group => group.partId === featureGroup.partId),
      };
      return [devGroup, ...getColorOnlyGroupObjects(devGroup, aiSettings)]
    });

    // Synthetic groups for the additional feature areas (Wet Area, Vanity Area).
    // These have no real layer/activeFeature, they only exist so dev users can
    // attach a partBody prompt to the area as a whole. The featureGroup name
    // mirrors the `featureArea` from FEATURE_AREAS and partId is its lowercase.
    const isBathroom = getCommonVisualizerType() === "bathroom";
    const customAreaGroups = isBathroom ? FEATURE_AREAS.map(({featureArea}) => {
      const partId = snakeCase(featureArea);
      return {
        featureGroup: {
          partId,
          partName: featureArea,
          dbName: featureArea,
        },
        partId,
        enabled: true,
        hideIfShowAllIsNotEnabled: false,
      };
    }) : [];

    return [...customAreaGroups, ...devGroups];
  }

  // When aiImportData.aiGroups[visualizerType] is present, it's the sole source of
  // truth for `enabled`: groups it lists come through with its enabled value, any
  // group it doesn't list comes through disabled.
  const importEnabledByPartId = hasImportAiGroups
    ? new Map(
      importAiGroups
        .filter(group => group && typeof group === "object" && group.partId != null)
        .map(group => [group.partId, group.enabled])
    )
    : null;

  return uniqBy(groupsList, (item) => item.partId)
    .filter(groupObject => groupObject?.featureGroup?.activeFeature)
    .map(groupObject => {
      groupObject.enabled = forDevSettings
        ? true
        : importEnabledByPartId
          ? importEnabledByPartId.has(groupObject.partId) ? importEnabledByPartId.get(groupObject.partId) : false
          : typeof groupObject.enabled === 'boolean' ? groupObject.enabled : true;
      return groupObject
    });
}

/**
 * @param onlyRenderGroups {array}
 * @param aiSendOnlySelectedGroupsAsAReference {boolean}
 * @param featureGroupsWithEnabledThumbnails {featureGroupInterface[]}
 * */
export const createThumbnailsCanvas = async (onlyRenderGroups = [],
                                             aiSendOnlySelectedGroupsAsAReference,
                                             featureGroupsWithEnabledThumbnails = null) => {
  const layout = getCurrentLayout();
  let layers = layout.getLayers(false, true);
  // console.log("layers before", layers);
  if (featureGroupsWithEnabledThumbnails) {
    layers = layers.filter(layer => featureGroupsWithEnabledThumbnails.some(fg => (fg.partId === layer.partId)));
  }
  // console.log("layers featureGroupsWithEnabledThumbnails", featureGroupsWithEnabledThumbnails, layers);
  if (Array.isArray(onlyRenderGroups) && onlyRenderGroups.length) {
    if (!featureGroupsWithEnabledThumbnails) { // we already checked
      layers = layers.filter(layer => {
        return !onlyRenderGroups.some(fg => (fg.partId === layer.partId) && fg.sendThumbnail === false)
      });
      // console.log("layers after onlyRenderGroups", layers);
    }
    if (aiSendOnlySelectedGroupsAsAReference) {
      layers = layers.filter(layer => onlyRenderGroups.some(fg => fg.partId === layer.partId));
    }
  }
  // console.log("layers final", layers);
  const allFeatureGroupActiveFeatures = layout.getActiveFeatures(true);
  const currentActiveFeatures = allFeatureGroupActiveFeatures.filter(f => {
    return f.cartFeature && layers.some(l => l.partId === f.partId)
  });
  console.log("currentActiveFeatures", currentActiveFeatures);

  const COLS = 4;
  const MARGIN = 20;
  const LABEL_HEIGHT = 48;
  const FONT_SIZE = 17;

  // Load all thumbnails first so we know their actual sizes
  const loadedImages = await Promise.all(
    currentActiveFeatures.map(async (f) => {
      try {
        return await loadThumbnail(f.cartFeature);
      } catch (e) {
        console.warn("[createThumbnailsCanvas] failed to load thumbnail for feature:", f.cartFeature, e);
        return null;
      }
    })
  );

  // Determine cell size from the largest loaded thumbnail
  const maxThumbW = loadedImages.reduce((max, img) => img ? Math.max(max, img.naturalWidth) : max, 0);
  const maxThumbH = loadedImages.reduce((max, img) => img ? Math.max(max, img.naturalHeight) : max, 0);
  const THUMB_W = maxThumbW || 400;
  const THUMB_H = maxThumbH || 400;

  const cols = Math.min(COLS, currentActiveFeatures.length);
  const rows = Math.ceil(currentActiveFeatures.length / COLS);
  const cellWidth = THUMB_W + MARGIN * 2;
  const cellHeight = THUMB_H + MARGIN * 2 + LABEL_HEIGHT;

  // 1) create canvas sized to content
  const canvas = document.createElement('canvas');
  canvas.width = cols * cellWidth;
  canvas.height = rows * cellHeight;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 3) draw each thumbnail on the canvas in a grid, with label below
  for (let i = 0; i < currentActiveFeatures.length; i++) {
    const activeFeature = currentActiveFeatures[i];
    const col = i % COLS;
    const row = Math.floor(i / COLS);

    const cellX = col * cellWidth;
    const cellY = row * cellHeight;

    const img = loadedImages[i];
    if (img) {
      ctx.drawImage(img, cellX + MARGIN, cellY + MARGIN, THUMB_W, THUMB_H);
    }

    const label = activeFeature.featureGroup?.dbName || activeFeature.featureGroup?.partName || '';
    ctx.fillStyle = '#000000';
    ctx.font = `bold ${FONT_SIZE}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(label, cellX + MARGIN + Math.floor(THUMB_W / 2), cellY + MARGIN + THUMB_H + FONT_SIZE + 4, THUMB_W);
  }

  // 4) get imageData in the same format as getImagePromiseFromLayout() returns
  const imageData = canvas.toDataURL('image/jpeg', 1);

  // const response = await fetch(imageData);
  // const blob = await response.blob();
  // const url = URL.createObjectURL(blob);
  // window.open(url, '_blank');

  return {
    "b64File": imageData.split(',')[1],
    "fileName": "productGrid.jpg"
  }
}
